import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { format } from "date-fns";

// Add the autotable plugin type to jsPDF
declare module "jspdf" {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
  }
}

// Generate PDF bill for a sale
export const generateSaleBill = (
  saleData: {
    customer: string;
    saleDate: string;
    items: Array<{
      productName: string;
      quantity: number;
      unitPrice: number;
      discount: number;
      total: number;
    }>;
    grandTotal: number;
    paymentMode: string;
    invoiceNumber: string;
  }
) => {
  const doc = new jsPDF();

  // Add company header
  doc.setFontSize(20);
  doc.setTextColor(40, 40, 40);
  doc.text("JAYA MARUTHI STATIONARY", 105, 20, { align: "center" });
  
  doc.setFontSize(12);
  doc.text("SALES INVOICE", 105, 30, { align: "center" });
  
  // Add invoice details
  doc.setFontSize(10);
  doc.text(`Invoice Date: ${format(new Date(saleData.saleDate), "MMM dd, yyyy")}`, 20, 45);
  doc.text(`Customer: ${saleData.customer}`, 20, 52);
  doc.text(`Invoice #: ${saleData.invoiceNumber}`, 20, 59);
  doc.text(`Payment Mode: ${saleData.paymentMode}`, 20, 66);
  
  // Add company details on the right
  doc.text("6F JC Street.", 190, 45, { align: "right" });
  doc.text("Dharnampet", 190, 52, { align: "right" });
  doc.text("Gudiyatham, 623602", 190, 59, { align: "right" });
  doc.text("+91 98943 61243", 190, 66, { align: "right" });

  // Add line
  doc.setDrawColor(220, 220, 220);
  doc.line(20, 72, 190, 72);
  
  // Create table with items
  const tableColumn = ["Item", "Quantity", "Unit Price", "Discount", "Total"];
  const tableRows = saleData.items.map(item => [
    item.productName,
    item.quantity.toString(),
    `${item.unitPrice.toFixed(2)}`,
    `${item.discount}%`,
    `${item.total.toFixed(2)}`
  ]);
  
  // Add items table
  doc.autoTable({
    head: [tableColumn],
    body: tableRows,
    startY: 77,
    theme: 'grid',
    styles: { fontSize: 9 },
    headStyles: { fillColor: [66, 139, 202] },
    margin: { left: 20, right: 20 },
  });
  
  // Add total
  const finalY = (doc as any).lastAutoTable.finalY + 10;
  doc.text("Subtotal:", 150, finalY);
  doc.text(`${saleData.grandTotal.toFixed(2)}`, 190, finalY, { align: "right" });
  
  doc.text("Tax:", 150, finalY + 7);
  doc.text("0.00", 190, finalY + 7, { align: "right" });
  
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Grand Total:", 150, finalY + 15);
  doc.text(`${saleData.grandTotal.toFixed(2)}`, 190, finalY + 15, { align: "right" });
  
  // Add footer
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text("Thank you for your business!", 105, finalY + 30, { align: "center" });
  
  // Save the PDF
  const filename = `Sale_Invoice_${format(new Date(), "yyyyMMdd_HHmmss")}.pdf`;
  doc.save(filename);
};

// Generate PDF bill for a purchase
export const generatePurchaseBill = (
  purchaseData: {
    vendor: string;
    purchaseDate: string;
    items: Array<{
      productName: string;
      quantity: number;
      unitPrice: number;
      discount: number;
      total: number;
    }>;
    grandTotal: number;
  }
) => {
  const doc = new jsPDF();
  
  // Add company header
  doc.setFontSize(20);
  doc.setTextColor(40, 40, 40);
  doc.text("JAYA MARUTHI STATIONARY", 105, 20, { align: "center" });
  
  doc.setFontSize(12);
  doc.text("PURCHASE ORDER", 105, 30, { align: "center" });
  
  // Add purchase order details
  doc.setFontSize(10);
  doc.text(`Order Date: ${format(new Date(purchaseData.purchaseDate), "MMM dd, yyyy")}`, 20, 45);
  doc.text(`Vendor: ${purchaseData.vendor}`, 20, 52);
  doc.text(`PO #: PO-${Date.now().toString().substring(7)}`, 20, 59);
  
  // Add company details on the right
  doc.text("6F JC Street", 190, 45, { align: "right" });
  doc.text("Dharnampet", 190, 52, { align: "right" });
  doc.text("Gudiyatham, 623602", 190, 59, { align: "right" });
  doc.text("+91 98943 61243", 190, 66, { align: "right" });
  
  // Add line
  doc.setDrawColor(220, 220, 220);
  doc.line(20, 72, 190, 72);
  
  // Create table with items
  const tableColumn = ["Item", "Quantity", "Unit Price", "Discount", "Total"];
  const tableRows = purchaseData.items.map(item => [
    item.productName,
    item.quantity.toString(),
    `${item.unitPrice.toFixed(2)}`,
    `${item.discount}%`,
    `${item.total.toFixed(2)}`
  ]);
  
  // Add items table
  doc.autoTable({
    head: [tableColumn],
    body: tableRows,
    startY: 70,
    theme: 'grid',
    styles: { fontSize: 9 },
    headStyles: { fillColor: [66, 139, 202] },
    margin: { left: 20, right: 20 },
  });
  
  // Add total
  const finalY = (doc as any).lastAutoTable.finalY + 10;
  doc.text("Subtotal:", 150, finalY);
  doc.text(`${purchaseData.grandTotal.toFixed(2)}`, 190, finalY, { align: "right" });
  
  doc.text("Tax:", 150, finalY + 7);
  doc.text("0.00", 190, finalY + 7, { align: "right" });
  
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Grand Total:", 150, finalY + 15);
  doc.text(`${purchaseData.grandTotal.toFixed(2)}`, 190, finalY + 15, { align: "right" });
  
  // Add footer
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text("This is a purchase order from Jaya Maruthi Stationary.", 105, finalY + 30, { align: "center" });
  
  // Save the PDF
  const filename = `Purchase_Order_${format(new Date(), "yyyyMMdd_HHmmss")}.pdf`;
  doc.save(filename);
};